import { useEffect } from 'react';

import { Input } from '@/components/Input';
import {
    selectDeposit,
    selectPurchasePrice,
    setDeposit,
} from '@/context/borrower';
import { selectPartner } from '@/context/display';
import { useAppDispatch, useAppSelector } from '@/context/storeHooks';
import { useFieldCopy } from '@/copy';

export interface IProps {
    baseId: string;
    showErrors: boolean;
}

export const testId = 'Store';

export function Deposit({ baseId, showErrors }: IProps): JSX.Element {
    // ***** Redux *****
    const fields = useFieldCopy();
    const currentState = useAppSelector(selectDeposit);
    const dispatch = useAppDispatch();
    const purchasePrice = useAppSelector(selectPurchasePrice);
    const partner = useAppSelector(selectPartner);

    const minimumDepositPercentage =
        (partner.minDepositPercent ?? 0) / 100 || 0;

    // ***** Event Handlers *****
    // Initialize the deposit to 10% of the purchase price on purchase price change
    useEffect(() => {
        if (!purchasePrice) return;

        const minDeposit = purchasePrice * minimumDepositPercentage;
        if (!currentState || currentState < minDeposit) {
            dispatch(setDeposit(minDeposit));
        }
    }, [purchasePrice]);

    // Set the deposit to 10% of the purchase price if the deposit is less than 10% of the purchase price
    function onBlur(): void {
        if (!purchasePrice) return;

        const minDeposit = purchasePrice * minimumDepositPercentage;
        if (!currentState || currentState < minDeposit) {
            dispatch(setDeposit(minDeposit));
        }
    }

    function handleChange(value: string): void {
        const deposit = parseFloat(value.replace(/[^0-9.]/g, ''));
        dispatch(setDeposit(deposit));
    }

    // ***** Render *****
    return (
        <div data-testid={testId} className='w-full'>
            <Input
                title={fields.deposit}
                required={false}
                placeholder=''
                // This field is not mandatory, so it is never flagged: a deposit is optional
                showErrors={false}
                onChange={handleChange}
                id={`${baseId}-input-deposit`}
                value={currentState?.toLocaleString() ?? ''}
                prefix='$'
                showNumpad={true}
                // tooltipContent='The amount of money that you are willing to pay upfront'
                onBlur={onBlur}
            />
        </div>
    );
}
